'use client';

import { Button } from '@common/components';
import { motion } from 'framer-motion';
import Link from 'next/link';
import { memo, useState } from 'react';

import { Image } from '@/components/client';
import { DONATION_ROUTES, WELLO_IMAGE_PREFIX_URL } from '@/constants';

import { FriendInviteShareDrawer } from './FriendInviteShareDrawer';

const PRODUCT_LIST_IMAGE = 'prod-list.png';

const ProductSlider = memo(function ProductSlider() {
  return (
    <div className="absolute left-1/2 top-1/2 h-130 w-[750px] -translate-x-1/2 translate-y-[calc(-50%+20px)] overflow-hidden">
      <motion.div
        className="flex h-full"
        initial={{ x: '0%' }}
        animate={{ x: '-50%' }}
        transition={{
          repeat: Infinity,
          repeatType: 'loop',
          duration: 20,
          ease: 'linear',
        }}
      >
        {[0, 1].map((index) => (
          <div key={index} className="relative aspect-[1920/89] h-130 shrink-0">
            <Image
              src={`${WELLO_IMAGE_PREFIX_URL}/static/event/home-love/market/${PRODUCT_LIST_IMAGE}`}
              alt="특산품 목록"
              fill
              className="object-contain"
              unoptimized
            />
          </div>
        ))}
      </motion.div>
    </div>
  );
});

export const FriendInviteSection = () => {
  const [isShareDrawerOpen, setIsShareDrawerOpen] = useState(false);

  return (
    <section
      id="friend-invite"
      className="flex w-full scroll-mt-46 flex-col items-center gap-32 overflow-hidden bg-R-600 px-24 py-60"
    >
      <div className="flex w-full max-w-[375px] flex-col items-center gap-8">
        <div className="rounded-xxlarge bg-O-100 px-20 py-6">
          <p className="text-center font-700 text-R-600 typo-body1">Event 3</p>
        </div>

        <div className="flex flex-col items-center gap-8 text-center">
          <p className="font-700 text-O-100 typo-h7">12월 친구 초대 이벤트</p>
          <h2 className="font-800 text-O-100 typo-h4">
            친구를 많이 초대할수록
            <br />
            경품에 당첨될 확률 UP!
          </h2>
        </div>
      </div>

      {/* 경품 안내 텍스트 */}
      <div className="flex w-full max-w-[375px] flex-col items-center text-center font-500 text-Gray-white/70 typo-h7">
        <p>초대한 친구가 가입 후 기부하면</p>
        <p>웰포인트 2,000원 + 경품 추첨 참여!</p>
        <p>친구는 가입만 해도 1,000원!</p>
      </div>

      {/* 1등: 지역별 특산품 20종 */}
      <div className="flex w-full flex-col items-center">
        {/* 선물 상자 이미지와 슬라이더 */}
        <div className="relative w-full max-w-[375px]">
          <div className="relative aspect-[375/328] w-full">
            <Image
              src={`${WELLO_IMAGE_PREFIX_URL}/static/event/home-love/market/price1-box.png`}
              alt="선물 상자"
              fill
              className="object-contain"
            />
          </div>

          {/* 상품 슬라이더 - 선물 상자 뒤에서 흐름 */}
          <ProductSlider />
        </div>

        {/* 1등 라벨 */}
        <div className="flex flex-col items-center gap-12 pt-16">
          <div className="rounded-xxlarge bg-O-100 px-12 py-4">
            <p className="font-700 text-R-600 typo-body1">1등</p>
          </div>
          <div className="flex items-center gap-4">
            <p className="font-700 text-O-100 typo-h5">지역별 특산품 20종</p>
            <p className="font-700 text-O-100 typo-body3">(1명)</p>
          </div>
        </div>

        <p className="text-center font-700 text-Gray-white/60 typo-caption1">
          *위 이미지는 예시이며, 지역별로 답례품 1종씩 선택하실 수 있습니다.
        </p>
      </div>

      {/* 경품 카드 영역 */}
      <div className="flex w-full max-w-[375px] flex-col items-center gap-32">
        {/* 2등: 아웃백 10만원 식사권 */}
        <div className="flex flex-col items-center gap-12">
          <div className="rounded-xxlarge bg-O-100 px-12 py-4">
            <p className="font-700 text-R-600 typo-body1">2등</p>
          </div>
          <div className="flex items-center gap-4">
            <p className="font-700 text-O-100 typo-h5">아웃백 10만원 식사권</p>
            <p className="font-700 text-O-100 typo-body3">(1명)</p>
          </div>
        </div>

        {/* 3등: 신세계 상품권 5만원권 */}
        <div className="flex flex-col items-center gap-12">
          <div className="rounded-xxlarge bg-O-100 px-12 py-4">
            <p className="font-700 text-R-600 typo-body1">3등</p>
          </div>
          <div className="flex items-center gap-4">
            <p className="font-700 text-O-100 typo-h5">신세계 상품권 5만원권</p>
            <p className="font-700 text-O-100 typo-body3">(1명)</p>
          </div>
        </div>
      </div>

      {/* 이벤트 주의사항 */}
      <ul className="flex w-full max-w-[375px] flex-col gap-4 font-400 text-Gray-white/70 typo-caption1">
        <li className="ml-18 list-disc">
          경품 추첨은 이벤트 기간이 종료된 후, 1월 내에 진행됩니다.
        </li>
        <li className="ml-18 list-disc">
          당첨자에 한해 개별적으로 연락드릴 예정이며, 상세 내용은 추후
          &apos;마이페이지 &gt; 공지사항&apos;을 확인해주시기 바랍니다.
        </li>
      </ul>

      {/* CTA 버튼 */}
      <div className="flex w-full max-w-[375px] gap-16">
        <Button
          className="flex-1 px-0"
          onClick={() => setIsShareDrawerOpen(true)}
        >
          <span className="flex w-full flex-col">
            <span>친구 초대하고</span>
            <span>웰포인트 받기</span>
          </span>
        </Button>
        <Button asChild className="flex-1 px-0">
          <Link href={DONATION_ROUTES.MARKET_INVITE.pathname}>
            <span className="w-full flex-col flex-center">
              <span>친구 초대 이벤트</span>
              <span>자세히 보기</span>
            </span>
          </Link>
        </Button>
      </div>

      <FriendInviteShareDrawer
        opened={isShareDrawerOpen}
        onClose={() => setIsShareDrawerOpen(false)}
      />
    </section>
  );
};
